import React from 'react';
import {
  Button, EmptyState, Table,
} from '@opendate-app/setlist';
import HouseholdGuests from './HouseholdGuests';

const DEFAULT_RELATIONSHIP_OPTIONS = ['spouse', 'partner', 'child', 'parent', 'sibling', 'caregiver', 'other'];

const fanNameFor = (fan) => fan.name || [fan.first_name, fan.last_name].filter(Boolean).join(' ') || '(No name)';

function HouseholdsTab({ team, householdsHook, relationshipOptions = [] }) {
  const relationshipChoices = relationshipOptions.length ? relationshipOptions : DEFAULT_RELATIONSHIP_OPTIONS;
  const {
    households,
    loading,
    error,
    openCreateModal,
    openAddMemberModal,
    promptRemoveMember,
    promptDeleteHousehold,
    handleChangeMemberLevel,
    handleChangeMemberRelationship,
    addGuest,
    removeGuest,
  } = householdsHook;

  return (
    <div className="mt-3">
      <div className="card">
        <div className="card-body p-0">
          <div className="border-bottom px-4 py-3 d-flex justify-content-between align-items-center">
            <h2 className="h6 mb-0">Households</h2>
            <div className="d-flex align-items-center">
              <button
                type="button"
                className="btn btn-setlist-primary btn-sm d-inline-flex align-items-center"
                onClick={openCreateModal}
              >
                <span className="me-1">+</span>
                <span>Create household</span>
              </button>
            </div>
          </div>

          {loading && (
            <div className="text-center text-muted py-4">Loading households...</div>
          )}

          {!loading && error && (
            <div className="text-center text-danger py-4">{error}</div>
          )}

          {!loading && !error && households.length === 0 && (
            <div className="text-center text-muted py-4">No households yet.</div>
          )}

          {!loading && !error && households.map((household) => {
            const memberships = household.household_memberships || [];
            const covering = household.covering_memberships || [];

            return (
              <div key={household.id} className="border-bottom px-4 py-3">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <h3 className="h6 mb-0">{household.name || '(Unnamed household)'}</h3>
                  <div className="d-flex align-items-center" style={{ gap: 12 }}>
                    <button
                      type="button"
                      className="btn btn-setlist-secondary btn-sm"
                      onClick={() => openAddMemberModal(household)}
                    >
                      + Add member
                    </button>
                    <button
                      type="button"
                      className="cdp-icon-btn-danger"
                      onClick={() => promptDeleteHousehold(household)}
                      aria-label="Delete household"
                    >
                      <i className="fal fa-trash" aria-hidden="true" />
                    </button>
                  </div>
                </div>

                {covering.length > 0 && (
                  <p className="text-muted small mb-2">
                    {'Everyone in this household is covered by '}
                    {covering.map((m, i) => (
                      <span key={m.id}>
                        {i > 0 && ' and '}
                        <strong>{m.name}</strong>
                        {' (paid by '}
                        {m.paid_by.deleted ? 'a deleted contact' : (
                          <Button as="a" href={`/teams/${team.id}/crm/fans/${m.paid_by.id}`} variant="link-color">{m.paid_by.name || '(No name)'}</Button>
                        )}
                        )
                      </span>
                    ))}
                  </p>
                )}

                <Table
                  columns={[
                    {
                      key: 'member',
                      header: 'Member',
                      primary: true,
                      render: (membership) => (
                        <Button as="a" href={`/teams/${team.id}/crm/fans/${membership.fan?.id}`} variant="link-color">
                          {fanNameFor(membership.fan || {})}
                        </Button>
                      ),
                    },
                    {
                      key: 'email',
                      header: 'Email',
                      render: (membership) => <span className="text-muted">{membership.fan?.email || '—'}</span>,
                    },
                    {
                      key: 'level',
                      header: 'Level',
                      width: '150px',
                      render: (membership) => (
                        <select
                          value={membership.level}
                          onChange={(e) =>
                            handleChangeMemberLevel(household, membership, e.target.value)
                          }
                          className="cdp-select"
                          style={{ width: 130 }}
                        >
                          <option value="primary">Primary</option>
                          <option value="secondary">Secondary</option>
                        </select>
                      ),
                    },
                    {
                      key: 'relationship',
                      header: 'Relationship',
                      width: '160px',
                      render: (membership) => (
                        <select
                          value={membership.relationship || ''}
                          onChange={(e) =>
                            handleChangeMemberRelationship(household, membership, e.target.value)
                          }
                          className="cdp-select"
                          style={{ width: 160 }}
                          aria-label="Member relationship"
                        >
                          <option value="">—</option>
                          {relationshipChoices.map((rel) => (
                            <option key={rel} value={rel}>
                              {rel.charAt(0).toUpperCase() + rel.slice(1)}
                            </option>
                          ))}
                        </select>
                      ),
                    },
                    {
                      key: 'actions',
                      header: '',
                      align: 'right',
                      width: '48px',
                      render: (membership) => (
                        <button
                          type="button"
                          className="cdp-icon-btn-danger"
                          onClick={() => promptRemoveMember(household, membership)}
                          aria-label="Remove member"
                        >
                          <i className="fal fa-times" aria-hidden="true" />
                        </button>
                      ),
                    },
                  ]}
                  data={memberships}
                  getRowKey={(membership) => membership.id}
                  empty={<EmptyState size="sm" title="No members in this household." />}
                />

                {household.authorized_guests && (
                  <HouseholdGuests household={household} addGuest={addGuest} removeGuest={removeGuest} />
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default HouseholdsTab;
